import { WrapUpCategory } from "@/features/wrap-up/enums/wrap-up.enum";
import type {
  WrapUp,
  WrapUpConfigOption,
  WrapUpConfigStep,
} from "@/features/wrap-up/types";
import type { ClosingNote, ClosingNoteField } from "@/types/closing-note";
import type { Lifecycle } from "@/types/lifecycle";
import type { SavedFile } from "@/types/saved-files";

import { createId, createPastTimestamp } from "./base";

import {
  WrapUpConfigFollowUpSuggestionType,
  WrapUpConfigStepType,
} from "@/features/wrap-up/enums/wrap-up.enum";

type ClosingNoteSeed = {
  category: string;
  description?: string;
  fields?: Array<Omit<ClosingNoteField, "id">>;
};

// Disposition categories for the clinic org. These are the options surfaced in
// the wrap-up's "Close conversation" step (their ids feed generateWrapUps).
const CLOSING_NOTE_SEEDS: ClosingNoteSeed[] = [
  {
    category: "Booked Appointment",
    description: "Lead booked a consultation or treatment.",
    fields: [
      {
        name: "Treatment",
        type: "string",
        position: 0,
        required: false,
        config: { placeholder: "e.g. Laser Hair Removal" },
      },
      {
        name: "Notes",
        type: "string",
        position: 1,
        required: false,
        config: { placeholder: "Add any details" },
      },
    ],
  },
  {
    category: "Spam",
    description: "Junk or irrelevant message, not a real lead.",
  },
  {
    category: "Called - No Answer",
    description: "Outbound call attempted, no answer.",
  },
  {
    category: "Called - Rejected",
    description: "Lead declined the call.",
  },
  {
    category: "Interested - Needs Follow up",
    description: "Lead is interested and needs a follow-up.",
  },
  {
    category: "Not Interested",
    description: "Lead is not interested at this time.",
    fields: [
      {
        name: "Reason",
        type: "select",
        position: 0,
        required: true,
        config: { options: ["Price", "Timing", "Location", "Other"] },
      },
    ],
  },
  {
    category: "Patient",
    description: "Already an existing patient.",
  },
  {
    category: "Duplicate",
    description: "Duplicate of another contact.",
  },
];

export function generateClosingNotes(): ClosingNote[] {
  return CLOSING_NOTE_SEEDS.map((seed) => ({
    id: createId(),
    category: seed.category,
    description: seed.description,
    fields: (seed.fields ?? []).map((field) => ({ ...field, id: createId() })),
    createdAt: createPastTimestamp(60),
  }));
}

const SAVED_FILE_SEEDS: Array<Pick<SavedFile, "name" | "url" | "type">> = [
  {
    name: "First visit guide.pdf",
    url: "/demo/first-visit-guide.pdf",
    type: "file",
  },
];

export function generateSavedFiles(): SavedFile[] {
  return SAVED_FILE_SEEDS.map((seed) => ({
    id: createId(),
    name: seed.name,
    url: seed.url,
    type: seed.type,
    createdAt: createPastTimestamp(45),
    updatedAt: createPastTimestamp(15),
    lastUsedAt: createPastTimestamp(2),
  }));
}

export function generateWrapUps(
  closingNoteIds: string[],
  lifecycles: Lifecycle[],
): WrapUp[] {
  const closeStep = (): WrapUpConfigStep => ({
    id: createId(),
    isAutomated: false,
    type: WrapUpConfigStepType.CLOSE_CONVERSATION,
    config: { closingNoteIds },
  });

  const followUpStep = (): WrapUpConfigStep => ({
    id: createId(),
    isAutomated: false,
    type: WrapUpConfigStepType.CREATE_FOLLOW_UP,
    config: {
      suggestions: [
        WrapUpConfigFollowUpSuggestionType.ONE_DAY_FROM_NOW,
        WrapUpConfigFollowUpSuggestionType.THREE_DAYS_FROM_NOW,
        WrapUpConfigFollowUpSuggestionType.ONE_WEEK_FROM_NOW,
      ],
    },
  });

  const createAppointmentStep = (): WrapUpConfigStep => ({
    id: createId(),
    isAutomated: false,
    type: WrapUpConfigStepType.CREATE_APPOINTMENT,
  });

  const updateAppointmentStep = (): WrapUpConfigStep => ({
    id: createId(),
    isAutomated: false,
    type: WrapUpConfigStepType.UPDATE_APPOINTMENT,
  });

  // Returns an update_lifecycle step for the first lifecycle matching the
  // predicate, or null when the seed doesn't have one (so the caller can drop
  // it instead of wiring a non-existent lifecycle id).
  const lifecycleStep = (
    predicate: (lifecycle: Lifecycle) => boolean,
  ): WrapUpConfigStep | null => {
    const lifecycle = lifecycles.find(predicate);

    if (!lifecycle) {
      return null;
    }

    return {
      id: createId(),
      isAutomated: false,
      type: WrapUpConfigStepType.UPDATE_LIFECYCLE,
      config: { toLifecycleId: lifecycle.id },
    };
  };

  const byName = (name: string) => (lifecycle: Lifecycle) =>
    lifecycle.name === name;

  const isStep = (step: WrapUpConfigStep | null): step is WrapUpConfigStep =>
    step !== null;

  const options: WrapUpConfigOption[] = [
    {
      id: createId(),
      name: "Booked Appointment",
      category: WrapUpCategory.WON,
      description: "Book the visit, advance the lifecycle, and close.",
      steps: [
        createAppointmentStep(),
        lifecycleStep(byName("Consultation Scheduled")),
        closeStep(),
      ].filter(isStep),
    },

    {
      id: createId(),
      name: "Interested - Needs Follow up",
      category: WrapUpCategory.IN_PROGRESS,
      description: "Set a follow-up reminder and keep the lead warm.",
      steps: [
        followUpStep(),
        lifecycleStep(byName("Contacted")),
        closeStep(),
      ].filter(isStep),
    },
    {
      id: createId(),
      name: "Not Interested",
      category: WrapUpCategory.LOST,
      description: "Mark the lead lost and close the conversation.",
      steps: [lifecycleStep((l) => Boolean(l.isLost)), closeStep()].filter(
        isStep,
      ),
    },
    {
      id: createId(),
      name: "Treatment completed",
      category: WrapUpCategory.WON,
      description: "Update the visit and close after a successful treatment.",
      steps: [updateAppointmentStep(), closeStep()],
    },
    {
      id: createId(),
      name: "Waiting for a decision",
      category: WrapUpCategory.IN_PROGRESS,
      description: "Give them time to decide and schedule a follow-up.",
      steps: [followUpStep(), closeStep()],
    },
    {
      id: createId(),
      name: "Requested a callback",
      category: WrapUpCategory.IN_PROGRESS,
      description: "Arrange another call at a better time.",
      steps: [followUpStep(), closeStep()],
    },
    {
      id: createId(),
      name: "Chose another provider",
      category: WrapUpCategory.LOST,
      description: "Record the decision and close the conversation.",
      steps: [lifecycleStep((l) => Boolean(l.isLost)), closeStep()].filter(
        isStep,
      ),
    },
    {
      id: createId(),
      name: "Quick Close",
      description: "Close the conversation without any follow-up actions.",
      steps: [closeStep()],
    },
  ];

  return [
    {
      id: createId(),
      name: "Standard Close",
      description: "Wrap up the conversation with the right outcome.",
      config: options,
      createdBy: null,
      createdAt: createPastTimestamp(30),
      updatedAt: createPastTimestamp(5),
    },
  ];
}
